<script>
import { mapGetters } from 'vuex'
import BoardResource from '@/resources/board'
import ReservationResource from '@/resources/reservation'
import LapTimeResource from '@/resources/laptime'
import messages from '@/mixins/messages'
import lucra from '@/mixins/lucra'
import { timeDateFormattedPretty } from '@/util'

export default {
    name: 'Grid',
    mixins: [messages, lucra],
    computed: {
        ...mapGetters(['userResource', 'userId', 'isApexClub', 'creditsBalance', 'locationSelected']),
        apexEnabled() {
            return this.$appConfigBool('APEX_CLUB_ENABLED', true)
        },
        apexClubName() {
            return this.$appConfig('APEX_CLUB_NAME', 'Apex Club')
        },
        nextCountdown() {
            if (!this.nextReservation) {
                return null
            }
            const ms = this.nextReservation.dateTime - this.now
            if (ms <= 0) {
                return 'On track now'
            }
            const mins = Math.round(ms / 60000)
            if (mins < 60) {
                return `In ${mins} min`
            }
            const hrs = Math.floor(mins / 60)
            if (hrs < 24) {
                return `In ${hrs}h ${mins % 60}m`
            }
            return timeDateFormattedPretty(this.nextReservation.attributes.time)
        },
    },
    data() {
        return {
            loading: true,
            now: new Date(),
            clockId: null,
            nextReservation: null,
            board: null,
            boardRows: [],
            cashBoards: [],
            joiningId: null,
            personalBest: null,
            lapCount: null,
            recentLaps: [],
            upcomingRaces: [],
            upcomingRacesShowBoard: false,
        }
    },
    methods: {
        timeDateFormattedPretty,
        cashUntil(entry) {
            const ct = entry.board.cashTournament
            if (!ct) {
                return ''
            }
            if (ct.starts_at && new Date(ct.starts_at) > new Date()) {
                return `tournament starts on ${this.timeDateFormattedPretty(ct.starts_at)}`
            }
            return ct.expires_at ? `goes until ${this.timeDateFormattedPretty(ct.expires_at)}` : ''
        },
        async loadNext() {
            try {
                const page = await ReservationResource.filter({ user: this.userId, ordering: 'time' })
                const cutoff = new Date()
                this.nextReservation = (page.resources || []).find((r) => {
                    try {
                        return r.dateTime > cutoff
                    } catch (e) {
                        return false
                    }
                })
            } catch (e) {
                this.nextReservation = null
            }
        },
        async loadCashBoards() {
            try {
                const page = await BoardResource.filter({ cash_tournament: 'open', ordering: 'grid' })
                const boards = (page.resources || []).slice(0, 3)
                this.cashBoards = await Promise.all(
                    boards.map(async (board) => {
                        let rows = []
                        try {
                            const result = await board.getResults({ count: 6 })
                            rows = result.resources || []
                        } catch (e) {
                            rows = []
                        }
                        return { board, rows }
                    })
                )
            } catch (e) {
                this.cashBoards = []
            }
        },
        async loadBoard() {
            if (this.cashBoards.length) {
                return
            }
            try {
                const page = await BoardResource.filter({ ordering: 'grid' })
                this.board = (page.resources || [])[0] || null
                if (this.board) {
                    const result = await this.board.getResults({ count: 6 })
                    this.boardRows = result.resources
                }
            } catch (e) {
                this.board = null
                this.boardRows = []
            }
        },
        async loadUpcomingRaces() {
            const boards = this.cashBoards.length ? this.cashBoards.map((entry) => entry.board) : this.board ? [this.board] : []
            const multipleBoards = boards.length > 1
            try {
                const perBoard = await Promise.all(boards.map(async (board) => (await board.getUpcomingRaces(2)).map((race) => ({ race, board }))))
                this.upcomingRaces = perBoard
                    .flat()
                    .sort((a, b) => a.race.dateTime - b.race.dateTime)
                    .slice(0, 4)
                this.upcomingRacesShowBoard = multipleBoards
            } catch (e) {
                this.upcomingRaces = []
            }
        },
        async loadLaps() {
            try {
                const page = await LapTimeResource.filter({ user: this.userId, approved: true, ordering: 'rank' })
                this.lapCount = page.count != null ? page.count : (page.resources || []).length
                this.personalBest = (page.resources || [])[0] || null
            } catch (e) {
                this.personalBest = null
            }
        },
        async loadRecentLaps() {
            try {
                const page = await LapTimeResource.filter({ user: this.userId, ordering: '-created' })
                this.recentLaps = (page.resources || []).slice(0, 6)
            } catch (e) {
                this.recentLaps = []
            }
        },
        async joinCash(entry) {
            const ct = entry.board.cashTournament
            if (!ct) {
                return
            }
            // Joining a cash tournament always needs a real, logged-in account (Lucra identity
            // linking, KYC, payouts all hang off the user record), so this is gated here rather
            // than relying on the button's own disabled state to keep an anonymous visitor out.
            if (!this.$store.getters.token) {
                this.$router.push({ name: 'signup', query: { redirect: this.$route.fullPath } })
                return
            }
            this.joiningId = entry.board.id
            // Held back until the iframe is actually closed. It sits at z-index 3000 while
            // open, so a message added while it's still up renders underneath it, unseen.
            let pendingMessage = null
            try {
                await this.$lucraPrepareDeposit()
                await this.$lucraOpen(this.$refs.lucraContainer, { hidden: true })
                await this.$lucraLinkUser()
                let result = await this.$lucraJoinTournament(ct.provider_matchup_id)
                if (result.needsDeposit) {
                    pendingMessage = ['info', 'Add funds to cover the buy-in, then select join again.']
                }
                if (result.locationError) {
                    pendingMessage = ['danger', 'We could not verify your location for a cash tournament. Try again with location services on.']
                } else if (result.joined) {
                    await entry.board.joinCashTournament()
                    pendingMessage = ['success', "You're in the tournament. Set laps at the simulator to climb the board."]
                    await this.loadCashBoards()
                }
            } catch (e) {
                pendingMessage = ['danger', this.getResponseMessageFromAxiosException(e)]
            }
            await this.$lucraCloseRoute()
            if (pendingMessage) {
                this.addMessage(...pendingMessage)
            }
            this.joiningId = null
        },
    },
    async mounted() {
        this.clockId = setInterval(() => (this.now = new Date()), 20000)
        await this.loadCashBoards()
        await Promise.all([this.loadNext(), this.loadBoard(), this.loadLaps(), this.loadRecentLaps()])
        await this.loadUpcomingRaces()
        this.loading = false
    },
    beforeDestroy() {
        clearInterval(this.clockId)
    },
}
</script>

<template>
    <div class="sa-page">
        <header-image kicker="Votre grille"
            ><h1>{{ userResource }}</h1></header-image
        >
        <div class="sa-page__inner" v-if="!loading">
            <messages />

            <div class="sa-stats mb-4">
                <component :is="isApexClub ? 'div' : 'router-link'" :to="isApexClub ? null : { name: 'subscription' }" class="sa-card">
                    <div class="sa-stat__value is-plain">{{ isApexClub ? 'APEX' : 'INVITÉ' }}</div>
                    <div class="sa-stat__label">Abonnement</div>
                </component>
                <div class="sa-card">
                    <div class="sa-stat__value">{{ creditsBalance != null ? creditsBalance : '0' }}</div>
                    <div class="sa-stat__label">Points Apex</div>
                </div>
                <div class="sa-card">
                    <div class="sa-stat__value">{{ lapCount != null ? lapCount : '0' }}</div>
                    <div class="sa-stat__label">Tours valides</div>
                </div>
                <div class="sa-card">
                    <div class="sa-stat__value sa-mono">{{ personalBest ? personalBest.attributes.hms : '--:--.---' }}</div>
                    <div class="sa-stat__label">Meilleur tour</div>
                </div>
            </div>

            <div class="grid__cols align-items-start">
                <section class="grid__col">
                    <template v-if="cashBoards.length && cashTournamentsLocationEnabled">
                        <p class="sa-overline d-block mb-2">Tournois en argent</p>
                        <div class="sa-card grid__cash mb-3" v-for="entry in cashBoards" :key="entry.board.id">
                            <div class="grid__cashhead d-flex align-items-baseline justify-content-between">
                                <span class="grid__cashname text-truncate">{{ entry.board }}</span>
                                <span class="grid__cashbuyin sa-mono" v-if="entry.board.cashTournament">${{ (entry.board.cashTournament.buy_in_amount_cents / 100).toFixed(2) }} de droit d'entrée</span>
                            </div>
                            <div class="small text-muted mb-2" v-if="entry.board.cashTournament && (entry.board.cashTournament.participant_count > 1 || cashUntil(entry))">
                                <span v-if="entry.board.cashTournament.participant_count > 1">{{ entry.board.cashTournament.participant_count }} en course</span>
                                <span v-if="entry.board.cashTournament.participant_count > 1 && cashUntil(entry)"> &middot; </span>
                                <span v-if="cashUntil(entry)">{{ cashUntil(entry) }}</span>
                            </div>

                            <div class="sa-lb" v-if="entry.rows.length">
                                <div class="sa-lb__head grid__lbhead"><span>Pos</span><span>Pilote</span><span>Meilleur</span></div>
                                <div class="sa-lb__row grid__lbrow" v-for="(row, i) in entry.rows" :key="i">
                                    <span class="sa-lb__pos">{{ String(row.attributes.rank || i + 1).padStart(2, '0') }}</span>
                                    <span class="sa-lb__driver">{{ row.attributes.name || row.attributes.display_name }}</span>
                                    <span class="sa-lb__num">{{ row.attributes.hms || (row.attributes.points != null ? row.attributes.points + ' PTS' : '') }}</span>
                                </div>
                            </div>
                            <div class="sa-empty" v-else>Aucun tour pour le moment. Soyez le premier au classement.</div>

                            <div class="grid__cashactions d-flex align-items-center flex-wrap mt-3">
                                <b-button size="sm" variant="secondary" :to="{ name: 'leaderboards', params: { id: entry.board.id } }">Voir le classement</b-button>
                                <b-badge variant="success" v-if="entry.board.cashTournament && entry.board.cashTournament.joined">Vous participez</b-badge>
                                <b-button size="sm" variant="primary" v-else-if="entry.board.cashTournament && entry.board.cashTournament.joinable" :disabled="joiningId === entry.board.id" @click="joinCash(entry)"> <b-spinner small v-if="joiningId === entry.board.id" /> {{ lucraNeedsDeposit ? 'Ajouter des fonds et participer' : 'Participer au tournoi' }} </b-button>
                                <b-badge variant="light" v-else">Bientôt disponible</b-badge>
                            </div>
                        </div>
                    </template>

                    <template v-else>
                        <p class="sa-overline d-block mb-2">Prochaine session</p>
                        <div class="sa-card" v-if="nextReservation">
                            <div class="grid__nextrow d-flex align-items-baseline justify-content-between">
                                <span class="grid__nextwhen sa-mono">{{ nextReservation.getTimeFormatted ? nextReservation.getTimeFormatted() : '' }}</span>
                                <span class="grid__nextcount text-uppercase">{{ nextCountdown }}</span>
                            </div>
                            <div class="small text-muted mt-1">{{ timeDateFormattedPretty(nextReservation.attributes.time) }}</div>
                            <div class="grid__nextactions d-flex mt-3">
                                <b-button size="sm" variant="primary" :to="{ name: 'reservations' }">Réserver à nouveau</b-button>
                                <b-button size="sm" variant="secondary" :to="{ name: 'accountProfile' }">Gérer</b-button>
                            </div>
                        </div>
                        <div class="sa-empty" v-else>
                            <p class="mb-3">Aucune session réservée. Le classement vous attend.</p>
                            <b-button variant="primary" :to="{ name: 'reservations' }">Réserver une session</b-button>
                        </div>
                    </template>

                    <p class="sa-overline d-block mb-2 mt-4">Tours récents</p>
                    <div class="sa-lb" v-if="recentLaps.length">
                        <div class="sa-lb__head grid__laphead"><span>Circuit</span><span>Tour</span><span class="d-none d-sm-block">Voiture</span></div>
                        <div class="sa-lb__row grid__laprow" v-for="(lap, i) in recentLaps" :key="i">
                            <span class="sa-lb__driver">{{ lap.attributes.track_name }}</span>
                            <span class="sa-lb__num">{{ lap.attributes.hms }}</span>
                            <span class="sa-mono text-muted d-none d-sm-block text-truncate">{{ lap.attributes.car_name }}</span>
                        </div>
                    </div>
                    <div class="sa-empty" v-else>Aucun tour enregistré pour le moment.</div>
                    <div class="mt-3">
                        <b-button size="sm" variant="secondary" :to="{ name: 'laps' }">Tous les tours</b-button>
                    </div>
                </section>

                <section class="grid__col">
                    <template v-if="cashBoards.length && cashTournamentsLocationEnabled">
                        <p class="sa-overline d-block mb-2">Prochaine session</p>
                        <div class="sa-card" v-if="nextReservation">
                            <div class="grid__nextrow d-flex align-items-baseline justify-content-between">
                                <span class="grid__nextwhen sa-mono">{{ nextReservation.getTimeFormatted ? nextReservation.getTimeFormatted() : '' }}</span>
                                <span class="grid__nextcount text-uppercase">{{ nextCountdown }}</span>
                            </div>
                            <div class="small text-muted mt-1">{{ timeDateFormattedPretty(nextReservation.attributes.time) }}</div>
                            <div class="grid__nextactions d-flex mt-3">
                                <b-button size="sm" variant="primary" :to="{ name: 'reservations' }">Réserver à nouveau</b-button>
                                <b-button size="sm" variant="secondary" :to="{ name: 'accountProfile' }">Gérer</b-button>
                            </div>
                        </div>
                        <div class="sa-empty" v-else>
                            <p class="mb-3">Aucune session réservée. Le classement vous attend.</p>
                            <b-button variant="primary" :to="{ name: 'reservations' }">Réserver une session</b-button>
                        </div>
                    </template>

                    <template v-if="!cashBoards.length || !cashTournamentsLocationEnabled">
                        <p class="sa-overline d-block mb-2">
                            Classement en direct <span v-if="board">&middot; {{ board }}</span>
                        </p>
                        <div class="sa-lb" v-if="boardRows.length">
                            <div class="sa-lb__head grid__lbhead"><span>Pos</span><span>Pilote</span><span>Meilleur</span></div>
                            <div class="sa-lb__row grid__lbrow" v-for="(row, i) in boardRows" :key="i">
                                <span class="sa-lb__pos">{{ String(row.attributes.rank || i + 1).padStart(2, '0') }}</span>
                                <span class="sa-lb__driver">{{ row.attributes.name || row.attributes.display_name }}</span>
                                <span class="sa-lb__num">{{ row.attributes.hms || (row.attributes.points != null ? row.attributes.points + ' PTS' : '') }}</span>
                            </div>
                        </div>
                        <div class="sa-empty" v-else>Aucune donnée de classement pour le moment.</div>
                        <div class="mt-3">
                            <b-button size="sm" variant="secondary" :to="{ name: 'leaderboards' }">Tous les classements</b-button>
                        </div>
                    </template>

                    <template v-if="upcomingRaces.length">
                        <p class="sa-overline d-block mb-2 mt-4">Courses à venir</p>
                        <div class="sa-note">
                            <div class="d-flex justify-content-between small mb-1" v-for="entry in upcomingRaces" :key="entry.race.id">
                                <span>{{ entry.race }}<span v-if="upcomingRacesShowBoard" class="text-muted"> &middot; {{ entry.board }}</span></span>
                                <span class="text-muted sa-mono">{{ timeDateFormattedPretty(entry.race.attributes.date_time) }}</span>
                            </div>
                        </div>
                    </template>

                    <template v-if="apexEnabled">
                        <p class="sa-overline d-block mb-2 mt-4">Abonnement</p>
                        <div class="sa-note sa-note--live" v-if="!isApexClub">
                            {{ apexClubName }} est notre abonnement : 20 % de réduction sur chaque réservation de simulation automobile ici, des invitations aux événements réservés aux membres, des soirées de course gratuites et une chance de se qualifier pour la série de courses {{ apexClubName }}.
                            <div class="mt-2">
                                <b-button size="sm" variant="primary" :to="{ name: 'accountSubscription' }">Rejoindre {{ apexClubName }}</b-button>
                            </div>
                        </div>
                        <div class="sa-note" v-else>Vous êtes membre de {{ apexClubName }}. Profitez de 20 % de réduction sur chaque réservation, d'invitations aux événements réservés aux membres, de soirées de course gratuites et des qualifications pour la série de courses {{ apexClubName }}.</div>
                    </template>
                </section>
            </div>
        </div>

        <div class="grid__loading d-flex align-items-center justify-content-center" v-else>
            <b-spinner />
        </div>

        <div ref="lucraContainer" class="grid__lucra"></div>
    </div>
</template>

<style lang="scss" scoped>
.grid__loading {
    min-height: 60vh;
}

.grid__cols {
    display: grid;
    grid-template-columns: 1.2fr 1fr;
    gap: 28px;
}
@media (max-width: 960px) {
    .grid__cols {
        grid-template-columns: 1fr;
    }
}
.grid__col {
    min-width: 0;
}

.grid__cash {
    min-width: 0;
    border: 1px solid color-mix(in srgb, var(--sos-gold) 45%, transparent);
}
.grid__cashhead {
    gap: 12px;
}
.grid__cashname {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-heading);
}
.grid__cashbuyin {
    flex: none;
    font-size: 13px;
    font-weight: 700;
    color: var(--sos-gold);
}
.grid__cashactions {
    gap: 10px;
}

.grid__nextrow {
    gap: 16px;
}
.grid__nextwhen {
    font-size: 24px;
    color: var(--text-heading);
    font-weight: 500;
}
.grid__nextcount {
    font-family: var(--font-display);
    font-weight: 700;
    letter-spacing: 0.1em;
    font-size: 13px;
    color: var(--teal);
}
.grid__nextactions {
    gap: 10px;
}

.grid__lbhead,
.grid__lbrow {
    grid-template-columns: 44px 1fr auto;
    gap: 14px;
}
.grid__laphead,
.grid__laprow {
    grid-template-columns: 1fr auto auto;
    gap: 14px;
}

.grid__lucra {
    position: fixed;
    inset: 0;
    z-index: 3000;
}
.grid__lucra:empty {
    display: none;
}
</style>
